import type { RouteCapability } from './route-access';
import type { IconName } from '@academy/ui';

export type PrimaryWorkspaceRouteId =
  | 'overview'
  | 'people'
  | 'groups'
  | 'curriculum'
  | 'schedule'
  | 'presentations'
  | 'deliveries'
  | 'learning'
  | 'assessment'
  | 'messages'
  | 'privacy'
  | 'access'
  | 'operations'
  | 'governance';

export type WorkspaceDataSensitivity =
  'low-sensitivity-summary' | 'personal' | 'high-sensitivity' | 'restricted-administration';

export type WorkspacePersistencePolicy =
  'allowlisted-session-summary' | 'memory-only' | 'prohibited';

export type WorkspaceRouteMetadata = {
  id: PrimaryWorkspaceRouteId;
  href: string;
  label: string;
  icon: IconName;
  capability: RouteCapability;
  queryNamespace: string;
  sensitivity: WorkspaceDataSensitivity;
  persistence: WorkspacePersistencePolicy;
  intentData: boolean;
  idleCode: boolean;
  idleData: boolean;
  moduleAliases: readonly RegExp[];
};

export const workspaceRouteQueryNamespaces = {
  overview: 'overview-dashboard',
  people: 'people-directory',
  groups: 'groups-workspace',
  curriculum: 'curriculum-plans',
  schedule: 'schedule',
  presentations: 'presentation-library',
  deliveries: 'deliveries-workspace',
  learning: 'learning-queue',
  assessment: 'assessment-workspace',
  messages: 'messages',
  privacy: 'privacy-participant',
  access: 'access-administration',
  operations: 'operations-audit',
  governance: 'academy-governance',
} as const satisfies Record<PrimaryWorkspaceRouteId, string>;

export const scheduleWorkspaceQueryNamespace = (request: unknown) =>
  `${workspaceRouteQueryNamespaces.schedule}:${JSON.stringify(request)}`;

export const messagingWorkspaceQueryNamespace = (input: {
  conversationId?: number;
  conversationCursor?: number;
  messageCursor?: number;
}) =>
  `${workspaceRouteQueryNamespaces.messages}:${input.conversationId ?? 'inbox'}:${input.conversationCursor ?? 'newest'}:${input.messageCursor ?? 'newest'}`;

export const primaryWorkspaceRouteMetadata: readonly WorkspaceRouteMetadata[] = [
  {
    id: 'overview',
    href: '/',
    label: 'Overview',
    icon: 'overview',
    capability: 'overview',
    queryNamespace: workspaceRouteQueryNamespaces.overview,
    sensitivity: 'personal',
    persistence: 'prohibited',
    intentData: false,
    idleCode: false,
    idleData: false,
    moduleAliases: [],
  },
  {
    id: 'people',
    href: '/people',
    label: 'People',
    icon: 'people',
    capability: 'people',
    queryNamespace: workspaceRouteQueryNamespaces.people,
    sensitivity: 'personal',
    persistence: 'prohibited',
    intentData: false,
    idleCode: true,
    idleData: false,
    moduleAliases: [/^\/people\/(?:teachers|students)\/[^/]+$/],
  },
  {
    id: 'groups',
    href: '/groups',
    label: 'Groups',
    icon: 'groups',
    capability: 'groups',
    queryNamespace: workspaceRouteQueryNamespaces.groups,
    sensitivity: 'personal',
    persistence: 'prohibited',
    intentData: false,
    idleCode: true,
    idleData: false,
    moduleAliases: [/^\/groups\/[^/]+$/],
  },
  {
    id: 'curriculum',
    href: '/curriculum/plans',
    label: 'Curriculum',
    icon: 'curriculum',
    capability: 'curriculum',
    queryNamespace: workspaceRouteQueryNamespaces.curriculum,
    sensitivity: 'low-sensitivity-summary',
    persistence: 'memory-only',
    intentData: false,
    idleCode: false,
    idleData: false,
    moduleAliases: [],
  },
  {
    id: 'schedule',
    href: '/schedule',
    label: 'Schedule',
    icon: 'schedule',
    capability: 'schedule',
    queryNamespace: workspaceRouteQueryNamespaces.schedule,
    sensitivity: 'personal',
    persistence: 'prohibited',
    intentData: true,
    idleCode: false,
    idleData: false,
    moduleAliases: [/^\/(?:sessions|assignments)\/[^/]+$/],
  },
  {
    id: 'presentations',
    href: '/studio/presentations',
    label: 'Presentations',
    icon: 'presentations',
    capability: 'presentationStudio',
    queryNamespace: workspaceRouteQueryNamespaces.presentations,
    sensitivity: 'low-sensitivity-summary',
    persistence: 'allowlisted-session-summary',
    intentData: true,
    idleCode: true,
    idleData: true,
    moduleAliases: [],
  },
  {
    id: 'deliveries',
    href: '/deliveries',
    label: 'Deliveries',
    icon: 'deliveries',
    capability: 'delivery',
    queryNamespace: workspaceRouteQueryNamespaces.deliveries,
    sensitivity: 'high-sensitivity',
    persistence: 'prohibited',
    intentData: false,
    idleCode: false,
    idleData: false,
    moduleAliases: [/^\/deliveries\/[^/]+$/],
  },
  {
    id: 'learning',
    href: '/learning',
    label: 'Learning',
    icon: 'learning',
    capability: 'learning',
    queryNamespace: workspaceRouteQueryNamespaces.learning,
    sensitivity: 'high-sensitivity',
    persistence: 'prohibited',
    intentData: true,
    idleCode: false,
    idleData: false,
    moduleAliases: [/^\/learning\/tasks\/[^/]+$/],
  },
  {
    id: 'assessment',
    href: '/assessment',
    label: 'Assessment',
    icon: 'assessment',
    capability: 'assessment',
    queryNamespace: workspaceRouteQueryNamespaces.assessment,
    sensitivity: 'high-sensitivity',
    persistence: 'prohibited',
    intentData: false,
    idleCode: false,
    idleData: false,
    moduleAliases: [],
  },
  {
    id: 'messages',
    href: '/messages',
    label: 'Messages',
    icon: 'messages',
    capability: 'messages',
    queryNamespace: workspaceRouteQueryNamespaces.messages,
    sensitivity: 'high-sensitivity',
    persistence: 'prohibited',
    intentData: true,
    idleCode: false,
    idleData: false,
    moduleAliases: [/^\/messages\/[^/]+$/],
  },
  {
    id: 'privacy',
    href: '/privacy',
    label: 'Privacy & safety',
    icon: 'privacy',
    capability: 'privacy',
    queryNamespace: workspaceRouteQueryNamespaces.privacy,
    sensitivity: 'high-sensitivity',
    persistence: 'prohibited',
    intentData: false,
    idleCode: false,
    idleData: false,
    moduleAliases: [],
  },
  {
    id: 'access',
    href: '/admin/access/accounts',
    label: 'Access',
    icon: 'access',
    capability: 'accessAdministration',
    queryNamespace: workspaceRouteQueryNamespaces.access,
    sensitivity: 'restricted-administration',
    persistence: 'prohibited',
    intentData: false,
    idleCode: false,
    idleData: false,
    moduleAliases: [],
  },
  {
    id: 'operations',
    href: '/operations/audit',
    label: 'Operations',
    icon: 'operations',
    capability: 'operations',
    queryNamespace: workspaceRouteQueryNamespaces.operations,
    sensitivity: 'restricted-administration',
    persistence: 'prohibited',
    intentData: false,
    idleCode: false,
    idleData: false,
    moduleAliases: [],
  },
  {
    id: 'governance',
    href: '/academy/settings',
    label: 'Governance',
    icon: 'governance',
    capability: 'academyAdministration',
    queryNamespace: workspaceRouteQueryNamespaces.governance,
    sensitivity: 'restricted-administration',
    persistence: 'prohibited',
    intentData: false,
    idleCode: false,
    idleData: false,
    moduleAliases: [],
  },
];

export const primarySidebarRouteIds = primaryWorkspaceRouteMetadata
  .filter((descriptor) => descriptor.id !== 'governance')
  .map((descriptor) => descriptor.id);

const pathnameOf = (href: string) => {
  try {
    const pathname = new URL(href, 'https://workspace.invalid').pathname;
    return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  } catch {
    return '/unknown';
  }
};

export const workspaceRouteMetadataById = (id: PrimaryWorkspaceRouteId) => {
  const descriptor = primaryWorkspaceRouteMetadata.find((candidate) => candidate.id === id);
  if (!descriptor) throw new Error(`Unknown primary workspace route: ${id}`);
  return descriptor;
};

export const workspaceRouteMetadataForHref = (href: string) => {
  const pathname = pathnameOf(href);
  return (
    primaryWorkspaceRouteMetadata.find((candidate) => candidate.href === pathname) ??
    primaryWorkspaceRouteMetadata.find((candidate) =>
      candidate.moduleAliases.some((pattern) => pattern.test(pathname)),
    ) ??
    null
  );
};

export const canonicalPrimaryWorkspaceHref = (href: string) =>
  workspaceRouteMetadataForHref(href)?.href ?? null;
